// pages/Pricing/index.jsx

import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useSelector } from "react-redux";
import { selectUserSubscription } from "../../store/slices/subscriptionSlice";
import PricingCard from "../../components/pricing/PricingCard";
import RequestForm from "../../components/subscription/RequestForm";
import { toast } from "sonner";
import {
	PricingWrapper,
	PricingHeader,
	PricingGrid,
	ModalOverlay,
	TabButton,
	PricingSection,
	PricingTabs,
	ActiveSubscriptionBanner,
} from "./styles";
import { subscriptionPlans } from "../../config/subscriptionPlans";
import SingleSessionPricing from "../../components/pricing/SingleSessionPricing";

const Pricing = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [selectedPlan, setSelectedPlan] = useState(null);
	const currentSubscription = useSelector(selectUserSubscription);
	const [pricingType, setPricingType] = useState("subscription");

	const handlePlanSelect = (plan) => {
		if (!user) {
			navigate("/login", {
				state: {
					returnUrl: "/pricing",
					selectedPlan: plan.id,
				},
			});
			return;
		}

		// If user has an active subscription, check if they can change
		if (currentSubscription?.status === "active") {
			if (!currentSubscription.canChangePlan) {
				toast.error(
					`You can only change your plan in the last 7 days of your current ${currentSubscription.plan.interval} period.`,
					{
						style: { padding: "1rem" },
					}
				);
				return;
			}
		}

		setSelectedPlan(plan);
	};

	return (
		<PricingWrapper>
			{currentSubscription?.status === "active" && (
				<ActiveSubscriptionBanner>
					<p>
						You currently have an active {currentSubscription.plan.name} plan.
					</p>
					<p>
						You can change your plan{" "}
						{currentSubscription.canChangePlan
							? "now"
							: "at the end of your current period"}
						.
					</p>
				</ActiveSubscriptionBanner>
			)}

			<PricingHeader>
				<h1>Choose Your Perfect Plan</h1>
				<p>Flexible photography packages for every need</p>

				<PricingTabs>
					<TabButton
						active={pricingType === "subscription"}
						onClick={() => setPricingType("subscription")}
					>
						Subscription Plans
					</TabButton>
					<TabButton
						active={pricingType === "single"}
						onClick={() => setPricingType("single")}
					>
						Single Sessions
					</TabButton>
				</PricingTabs>
			</PricingHeader>

			<PricingSection>
				{pricingType === "subscription" ? (
					<PricingGrid>
						<PricingCard
							plan={subscriptionPlans.basic}
							onSelect={handlePlanSelect}
							isPopular={false}
						/>
						<PricingCard
							plan={subscriptionPlans.premium}
							onSelect={handlePlanSelect}
							isPopular={true}
						/>
						<PricingCard
							plan={subscriptionPlans.vip}
							onSelect={handlePlanSelect}
							isPopular={false}
						/>
					</PricingGrid>
				) : (
					<SingleSessionPricing />
				)}
			</PricingSection>

			<AnimatePresence>
				{selectedPlan && (
					<ModalOverlay
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={(e) => {
							if (e.target === e.currentTarget) {
								setSelectedPlan(null);
							}
						}}
					>
						<RequestForm
							selectedPlan={selectedPlan}
							onBack={() => setSelectedPlan(null)}
						/>
					</ModalOverlay>
				)}
			</AnimatePresence>
		</PricingWrapper>
	);
};

export default Pricing;
